import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { FadeImage } from '@design-system/components/FadeImage'
import { motion, useReducedMotion } from 'framer-motion'

import { UserAvatar } from '@/components/ui/UserAvatar'
import { cn } from '@/lib/utils'
import { personaThumbUrl } from '@/utils/personaThumbUrl'

import {
  buildPortraitMosaicTiles,
  mosaicColumnCount,
  mosaicThumbSize,
  MOSAIC_COLUMN_COUNT_FALLBACK,
  PORTRAIT_MOSAIC_FALLBACK_AVATAR_CLASS,
  PORTRAIT_MOSAIC_TILE_ASPECT_CLASS,
  MosaicThumbSize,
  PortraitMosaicResponse,
  PortraitMosaicTile,
} from './buildPortraitMosaicTiles'
import { SELECTION_RESUME_DELAY_MS, tilePopInTransition, TILE_POP_IN_VARIANTS } from './motion'
import { PortraitMosaicKpi, PortraitMosaicKpiData } from './PortraitMosaicKpi'
import { PortraitMosaicQuote, PortraitMosaicQuoteBlock } from './PortraitMosaicQuoteBlock'
import { resolveMosaicPickerImages } from './resolveMosaicPickerImages'

export {
  PORTRAIT_MOSAIC_TILE_CAP,
  PORTRAIT_MOSAIC_TILE_ASPECT_CLASS,
  PORTRAIT_MOSAIC_FALLBACK_AVATAR_CLASS,
  MOSAIC_COLUMN_COUNT_FALLBACK,
  MOSAIC_TARGET_TILE_PX,
  MOSAIC_MIN_COLUMNS,
  MOSAIC_MAX_COLUMNS,
  buildPortraitMosaicTiles,
  mosaicColumnCount,
  mosaicThumbSize,
  mosaicTileCountForFullRows,
} from './buildPortraitMosaicTiles'
export { PortraitMosaicKpi } from './PortraitMosaicKpi'
export {
  PortraitMosaicQuoteBlock,
  PORTRAIT_MOSAIC_QUOTE_BLOCK_HEIGHT_CLASS,
  PORTRAIT_MOSAIC_QUOTE_CLAMP_CLASS,
} from './PortraitMosaicQuoteBlock'
export { resolveMosaicPickerImages } from './resolveMosaicPickerImages'
export type { PortraitMosaicResponse, PortraitMosaicTile } from './buildPortraitMosaicTiles'
export type { PortraitMosaicKpiData } from './PortraitMosaicKpi'
export type { PortraitMosaicQuote } from './PortraitMosaicQuoteBlock'

/**
 * Renderable modes the tile system will grow into. S1 (this file) only ships
 * the base grid — S2 builds the rotating-quote chrome for `'quotes'`, S3 the
 * headline chrome for `'kpi'`. `'roll'` (Variant R) is parked, D7.
 */
export type PortraitMosaicMode = 'quotes' | 'kpi' | 'roll'

/**
 * The crowd is black and white; only the person currently speaking is in color.
 *
 * This is the single place that rule is encoded (live QA 2026-09-02, "pics should be black
 * and white no?"). It holds no matter what the caller highlights: `highlightedPersonaIds`
 * (a cut/segment filter) and `kpi.memberPersonaIds` (the metric's own membership, which
 * `StudyDocMagazineBand` forwards as the highlight set) can be most or all of the panel,
 * and reading "highlighted" as "full color" lit the entire wall and reduced the speaker
 * ring to decoration. Membership now expresses itself as a brightness step INSIDE the
 * black-and-white treatment — members read brighter than the rest of the crowd — and color
 * stays reserved for the one ringed speaker.
 *
 * Values follow `docs/plans/previews/portrait-mosaic-reference.html`: crowd
 * `filter: grayscale(1); opacity: .4`, speaker `grayscale(0); opacity: 1`. The member step
 * (`opacity-75`) sits between the two.
 */
export function portraitMosaicTileStateClass(state: {
  isHighlighted: boolean
  isSpeaker: boolean
}): string {
  if (state.isSpeaker) {
    return 'opacity-100'
  }
  return state.isHighlighted ? 'grayscale opacity-75' : 'grayscale opacity-40'
}

/**
 * Band tone context. Steers the tile placeholder background (platform-deep vs the neutral
 * `bg-muted` this component already used); `StudyDocMagazineBand` maps the same value onto
 * the band surface and ink tokens.
 *
 * `'auto'` is the default the band now uses: the surface follows the app's light/dark
 * theme through the `--card` token pair (light slab in light mode, deep slab in dark
 * mode), and the tile placeholder uses the equally theme-adaptive `bg-muted`. `'dark'` and
 * `'light'` stay available to pin a band to one tone regardless of theme.
 */
export type PortraitMosaicTone = 'dark' | 'light' | 'auto'

/**
 * Prop contract (additive only — see docs/plans/portrait-mosaic-strip-ui.md
 * and docs/plans/portrait-mosaic-design-upgrade.md D2/D5/D6).
 *
 * - `responses` / `highlightedPersonaIds` / `populationId` / `pickerImages` /
 *   `className`: unchanged from the landed base — panel data, the lit-tile
 *   predicate, and the picker-image fallback wiring behave exactly as before.
 * - `speakerPersonaId`: optional. When it matches a rendered tile, that tile
 *   renders the accent inset ring and carries `data-speaker="true"`. In
 *   `'quotes'` mode with a `quotes` array, the active quote's `personaId`
 *   takes over as the effective speaker (see `quotes` below); S3 (kpi) will
 *   drive this prop directly from the featured respondent. Omitted with no
 *   active quote → no tile is ringed (today's look).
 * - `mode`: optional, defaults to `'quotes'`. `'kpi'`/`'roll'` are still typed
 *   no-ops (S3/roll) — the grid renders identically for those values.
 * - `quotes`: optional. S2 (quotes mode). Rendered as a rotating quote block
 *   beneath the strip ONLY when the effective mode is `'quotes'` and this
 *   array is non-empty — omitted (or `mode !== 'quotes'`) renders byte-
 *   identical to S1, no new DOM. Each quote's `personaId` (`personas.token`)
 *   becomes the active speaker while it is showing: that tile is forced lit
 *   (added to the highlight set if not already present) and ringed; a quote
 *   whose `personaId` matches no rendered tile just renders un-ringed. See
 *   {@link PortraitMosaicQuote} for the `attribution` formatting contract.
 * - `tone`: optional. See {@link PortraitMosaicTone}. Omitted preserves
 *   today's rendering.
 * - `kpi`: optional. S3 (kpi mode). Rendered as the KPI headline + sub-line
 *   above the grid and a supporting quote below it ONLY when the effective
 *   mode is `'kpi'` — omitted (or `mode !== 'kpi'`) renders byte-identical
 *   to today, no new DOM. The lit set / speaker ring stay on the existing
 *   `highlightedPersonaIds`/`speakerPersonaId` props (D4: the caller wires
 *   `highlightedPersonaIds={kpi.memberPersonaIds}` itself) — this prop does
 *   not change tile dimming. The supporting quote is picked from `quotes`
 *   (whichever entry's `personaId` matches the effective speaker, else the
 *   first one) and rendered through the same `PortraitMosaicQuoteBlock` S2
 *   uses for `'quotes'` mode.
 */
interface PortraitMosaicStripProps {
  responses: Array<PortraitMosaicResponse>
  highlightedPersonaIds?: Array<string>
  /** When set, tiles missing a row image ask the slim picker view — never getPersonas. */
  populationId?: string
  /** Pre-resolved picker urls keyed by persona_id. Wins over a later fetch. */
  pickerImages?: Record<string, string>
  /** Persona id of the tile that should render the accent speaker ring. See prop contract above. */
  speakerPersonaId?: string
  /** Mode surface (S2 quotes / S3 kpi / roll parked). See prop contract above. */
  mode?: PortraitMosaicMode
  /** Rotating quotes for `'quotes'` mode; KPI mode's supporting quote also comes from here. See prop contract above. */
  quotes?: Array<PortraitMosaicQuote>
  /**
   * How many LEADING entries of `quotes` the idle rotation and the "next quote" advance
   * cycle through. Lets a caller append extra, non-curated quotes (e.g. every respondent's
   * own open-end answer) after a curated subset without the idle rotation ever landing on
   * one — a tile click can still select ANY entry via `quoteIndexByPersonaId` below, curated
   * or not. Clamped to `[1, quotes.length]`; omitted falls back to `quotes.length`, i.e.
   * today's behavior (rotate the whole array).
   */
  rotationCount?: number
  /** KPI headline data for `'kpi'` mode. See prop contract above. */
  kpi?: PortraitMosaicKpiData
  /** Band tone context. See prop contract above. */
  tone?: PortraitMosaicTone
  className?: string
}

export function PortraitMosaicStrip(props: PortraitMosaicStripProps) {
  const {
    responses,
    highlightedPersonaIds,
    populationId,
    pickerImages,
    speakerPersonaId,
    mode,
    quotes,
    rotationCount,
    kpi,
    tone,
    className,
  } = props

  const [fetchedPickerImages, setFetchedPickerImages] = useState<Record<string, string>>({})
  const [activeQuoteIndex, setActiveQuoteIndex] = useState(0)
  const [columnCount, setColumnCount] = useState(MOSAIC_COLUMN_COUNT_FALLBACK)
  const [thumbSize, setThumbSize] = useState<MosaicThumbSize>(mosaicThumbSize(0, 1))
  const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(null)
  const [isSelectionPaused, setIsSelectionPaused] = useState(false)
  const [selectionTick, setSelectionTick] = useState(0)
  const stripRef = useRef<HTMLDivElement>(null)

  const effectiveMode = mode ?? 'quotes'

  // Persona → the quote they speak. Drives BOTH which tiles are clickable (a tile with no
  // verbatim has nothing to swap in, so it stays inert) and which quote a click activates.
  // First quote per persona wins, matching the rotation order.
  const quoteIndexByPersonaId = useMemo(() => {
    const index = new Map<string, number>()
    ;(quotes ?? []).forEach((quote, quoteIndex) => {
      const personaId = (quote.personaId || '').trim()
      if (personaId && !index.has(personaId)) {
        index.set(personaId, quoteIndex)
      }
    })
    return index
  }, [quotes])

  // A click pins its speaker and pauses rotation; rotation resumes on its own after the
  // idle window, so the band never gets stuck on one face because someone clicked once.
  useEffect(() => {
    if (!isSelectionPaused) {
      return
    }
    const timer = window.setTimeout(() => {
      setIsSelectionPaused(false)
      setSelectedPersonaId(null)
    }, SELECTION_RESUME_DELAY_MS)
    return () => window.clearTimeout(timer)
    // `selectionTick` restarts the window on every click, including a repeat click on the
    // same persona (where the state values alone would not change).
  }, [isSelectionPaused, selectionTick])

  /** One reader-driven selection: show this quote, pin its speaker, hold the rotation. */
  function selectQuote(quoteIndex: number, personaId: string | null) {
    setActiveQuoteIndex(quoteIndex)
    setSelectedPersonaId(personaId)
    setIsSelectionPaused(true)
    setSelectionTick((current) => current + 1)
  }

  function handleSelectPersona(personaId: string) {
    const quoteIndex = quoteIndexByPersonaId.get(personaId)
    if (quoteIndex === undefined) {
      return
    }
    selectQuote(quoteIndex, personaId)
  }

  // Rotation window: only the leading `rotationCount` entries of `quotes` are cycled by
  // the idle timer and the advance button — extra entries appended after it (e.g. every
  // respondent's own row quote) are reachable only via a tile click. Clamped so it is
  // never 0 (divide-by-zero) and never past `quotes.length`; omitted falls back to
  // `quotes.length`, i.e. today's behavior (rotate everything).
  const effectiveRotationCount =
    quotes && quotes.length > 0 ? Math.max(1, Math.min(rotationCount ?? quotes.length, quotes.length)) : 0

  // Clicking the quote itself advances to the next quoted person — same selection
  // semantics as clicking their tile (ring + color move, rotation holds for the idle
  // window). A quote with no persona still advances and still pauses; it just pins no
  // tile, which is why the pause is its own flag rather than "a persona is selected".
  function handleAdvanceQuote() {
    if (!quotes || quotes.length === 0 || effectiveRotationCount === 0) {
      return
    }
    const nextIndex = (safeActiveQuoteIndex + 1) % effectiveRotationCount
    selectQuote(nextIndex, quotes[nextIndex]?.personaId ?? null)
  }

  const hasQuotes = effectiveMode === 'quotes' && Boolean(quotes && quotes.length > 0)
  const safeActiveQuoteIndex =
    hasQuotes && quotes ? Math.min(activeQuoteIndex, quotes.length - 1) : 0
  const activeQuote = hasQuotes && quotes ? quotes[safeActiveQuoteIndex] : undefined
  const activeSpeakerPersonaId = activeQuote?.personaId

  // Active speaker is forced lit while its quote shows. A CALLER-PROVIDED
  // `highlightedPersonaIds` array (e.g. a cut/segment filter) is respected as
  // given — it only grows to include the speaker if not already present, so
  // the caller's own highlight set stays stable otherwise.
  //
  // When the caller omits `highlightedPersonaIds` entirely AND there is an
  // active rotating speaker, the crowd must still read as a crowd (Step 5b
  // visual QA finding 2: "every tile renders full color — the dim/lit
  // distinction, the entire point of the wall, is absent"). Treating an
  // omitted prop as "everyone lit" here would light the whole panel and
  // reduce the ring to decoration. So the base set starts EMPTY (dim
  // everyone) and only the active speaker is added — matching the reference
  // (`docs/plans/previews/portrait-mosaic-reference.html` Variant A: one
  // tile lit + ringed at a time, never the whole wall). This only changes the
  // omitted-prop case; an explicitly empty array (`[]`, "dim everyone, no
  // exceptions") is left alone below since a speaker forces itself lit either
  // way. With no active speaker (no quotes / kpi mode), an omitted prop still
  // means "everyone lit" — unchanged.
  const effectiveHighlightedPersonaIds = useMemo(() => {
    if (!activeSpeakerPersonaId) {
      return highlightedPersonaIds
    }
    const base = highlightedPersonaIds ?? []
    if (base.includes(activeSpeakerPersonaId)) {
      return base
    }
    return [...base, activeSpeakerPersonaId]
  }, [highlightedPersonaIds, activeSpeakerPersonaId])

  const effectiveSpeakerPersonaId = activeSpeakerPersonaId ?? speakerPersonaId

  // S3 kpi mode (D4): the lit set is `kpi.memberPersonaIds`, passed straight
  // through the existing highlight mechanism above — `effectiveHighlightedPersonaIds`
  // is untouched by kpi mode (its speaker-growth branch only fires for the
  // quotes-mode `activeSpeakerPersonaId`, which is always undefined here), so
  // it collapses to the raw `highlightedPersonaIds` prop; kpi mode only
  // supplies a fallback when the caller omitted that prop. Same for the
  // speaker ring: `speakerPersonaId` wins when given, else the first member.
  // Everything else (dimming, ring rendering) stays on the props already
  // read by `buildPortraitMosaicTiles`/`PortraitMosaicTileView` above.
  const isKpiMode = effectiveMode === 'kpi' && Boolean(kpi)
  const tileHighlightedPersonaIds =
    isKpiMode && highlightedPersonaIds === undefined && kpi?.memberPersonaIds
      ? kpi.memberPersonaIds
      : effectiveHighlightedPersonaIds
  // A clicked tile wins the ring in kpi mode too: quotes mode already routes the click
  // through `activeQuoteIndex` (which drives `activeSpeakerPersonaId` above), but kpi mode
  // has no rotation, so the selection has to be read directly here.
  const tileSpeakerPersonaId = isKpiMode
    ? selectedPersonaId ?? effectiveSpeakerPersonaId ?? kpi?.memberPersonaIds?.[0]
    : effectiveSpeakerPersonaId
  // The supporting quote must belong to a lit tile — an unrestricted `?? quotes[0]`
  // fallback could surface a quote whose speaker isn't even rendered as a member of
  // the KPI's own lit set. Prefer the ringed speaker's quote; otherwise fall back to
  // any quote whose personaId is a lit member; render none when nothing qualifies.
  // `tileHighlightedPersonaIds === undefined` means "every tile lit" (the omitted-prop
  // convention `buildPortraitMosaicTiles` already honors), so any quote with a
  // personaId qualifies in that case.
  const kpiQuote =
    isKpiMode && quotes && quotes.length > 0
      ? quotes.find((candidate) => candidate.personaId === tileSpeakerPersonaId) ??
        quotes.find(
          (candidate) =>
            Boolean(candidate.personaId) &&
            (tileHighlightedPersonaIds === undefined ||
              tileHighlightedPersonaIds.includes(candidate.personaId as string)),
        )
      : undefined

  const baseTiles = useMemo(
    () =>
      buildPortraitMosaicTiles(responses, {
        highlightedPersonaIds: tileHighlightedPersonaIds,
        pickerImages,
        columns: columnCount,
      }),
    [responses, tileHighlightedPersonaIds, pickerImages, columnCount],
  )

  useLayoutEffect(() => {
    const node = stripRef.current
    if (!node) {
      return
    }
    const element = node

    function updateColumns() {
      const width = element.getBoundingClientRect().width
      const nextColumns = mosaicColumnCount(width)
      setColumnCount((current) => (nextColumns === current ? current : nextColumns))
      // Thumbnail variant follows the PHYSICAL tile size (CSS px × devicePixelRatio):
      // a full-viewport band on a retina display renders ~80css/160dev px tiles,
      // where t64 is visibly pixelated (live QA 2026-09-03).
      setThumbSize((current) => {
        const next = mosaicThumbSize(
          width > 0 ? width / nextColumns : 0,
          typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1,
        )
        return next === current ? current : next
      })
    }

    updateColumns()

    if (typeof ResizeObserver === 'undefined') {
      return
    }

    const observer = new ResizeObserver(updateColumns)
    observer.observe(node)
    return () => observer.disconnect()
  }, [baseTiles.length])

  // Tiles still missing an image after the response payload + `pickerImages`
  // prop. This is derived from `baseTiles` but — unlike `baseTiles` itself —
  // its CONTENT never changes just because the rotating active quote merges
  // its speaker into `effectiveHighlightedPersonaIds`: highlight state has no
  // bearing on which tiles lack an image. `missingPickerTiles` still gets a
  // new array identity on every rotation tick (it's re-filtered from a
  // `baseTiles` that itself has a new identity), but `missingPickerKey` below
  // collapses that down to a plain string, which is what the fetch effect
  // actually keys on.
  const missingPickerTiles = useMemo(
    () => baseTiles.filter((tile) => !tile.imageUrl),
    [baseTiles],
  )
  const missingPickerKey = useMemo(
    () =>
      missingPickerTiles
        .map((tile) => tile.personaId)
        .sort()
        .join(','),
    [missingPickerTiles],
  )

  useEffect(() => {
    if (!populationId || missingPickerTiles.length === 0) {
      setFetchedPickerImages({})
      return
    }

    let cancelled = false

    void resolveMosaicPickerImages(
      populationId,
      missingPickerTiles.map((tile) => ({ personaId: tile.personaId, name: tile.name })),
    ).then((next) => {
      if (!cancelled) {
        setFetchedPickerImages(next)
      }
    })

    return () => {
      cancelled = true
    }
    // Keyed on `missingPickerKey` (a stable, sorted, joined string of missing
    // persona ids) rather than `baseTiles`'s object identity — see
    // docs/plans/portrait-mosaic-design-upgrade.md (fable execution review,
    // finding 1). `baseTiles` gets a NEW array identity every ~4s as the
    // rotating quote's speaker is merged into the highlight set, even though
    // the set of tiles missing an image never actually changes; keying the
    // effect on that identity re-fired an identical `getPersonasForPicker`
    // request on every rotation tick. `missingPickerTiles` is read from the
    // closure intentionally (not listed as a dependency): by the time this
    // effect body runs, its content is exactly what `missingPickerKey`
    // already gates on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [populationId, missingPickerKey])

  const tiles = useMemo(() => {
    return baseTiles.map((tile) => {
      if (tile.imageUrl) {
        return tile
      }
      const pickerUrl = fetchedPickerImages[tile.personaId]
      if (!pickerUrl) {
        return tile
      }
      return { ...tile, imageUrl: pickerUrl, imageSource: 'picker' as const }
    })
  }, [baseTiles, fetchedPickerImages])

  if (tiles.length === 0) {
    return null
  }

  const grid = (
    <div
      ref={stripRef}
      className={cn('grid w-full gap-0.5 overflow-hidden', className)}
      style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}
      data-testid="portrait-mosaic-strip"
      data-columns={columnCount}
    >
      {tiles.map((tile, index) => (
        <PortraitMosaicTileView
          key={tile.personaId}
          tile={tile}
          index={index}
          isSpeaker={
            Boolean(tileSpeakerPersonaId) && tile.personaId === tileSpeakerPersonaId
          }
          onSelect={
            quoteIndexByPersonaId.has(tile.personaId) ? handleSelectPersona : undefined
          }
          tone={tone}
          thumbSize={thumbSize}
        />
      ))}
    </div>
  )

  if (isKpiMode && kpi) {
    return (
      <PortraitMosaicKpi kpi={kpi} tone={tone} quote={kpiQuote}>
        {grid}
      </PortraitMosaicKpi>
    )
  }

  return (
    <>
      {grid}
      {hasQuotes && quotes ? (
        <PortraitMosaicQuoteBlock
          quotes={quotes}
          activeIndex={safeActiveQuoteIndex}
          setActiveIndex={setActiveQuoteIndex}
          isPaused={isSelectionPaused}
          onAdvance={handleAdvanceQuote}
          rotationCount={effectiveRotationCount}
        />
      ) : null}
    </>
  )
}

interface PortraitMosaicTileViewProps {
  tile: PortraitMosaicTile
  index: number
  /** Renders the accent inset ring and `data-speaker="true"` when set. */
  isSpeaker: boolean
  /**
   * Set only for a persona who actually has a verbatim to swap in: the tile then renders
   * as a real button (focusable, keyboard-activatable, `cursor-pointer`). Omitted → the
   * tile is inert with no pointer affordance, so the crowd never looks clickable when
   * clicking it would do nothing.
   */
  onSelect?: (personaId: string) => void
  tone?: PortraitMosaicTone
  /** Pre-generated thumbnail variant sized to the measured tile (physical px). */
  thumbSize: MosaicThumbSize
}

function PortraitMosaicTileView(props: PortraitMosaicTileViewProps) {
  const { tile, index, isSpeaker, onSelect, tone, thumbSize } = props

  const shouldReduceMotion = useReducedMotion()
  const [hasFailed, setHasFailed] = useState(false)
  // One-shot fallback (finding 7, image compression): try the pre-generated
  // thumbnail first; if that variant 404s (not backfilled yet for this
  // persona), retry the original full-size url exactly once before giving up
  // to the initials avatar. `thumbUrl === tile.imageUrl` (non-matching host,
  // no rewrite happened) skips straight to the avatar instead of a pointless
  // identical retry.
  const [thumbFailed, setThumbFailed] = useState(false)

  const thumbUrl = tile.imageUrl ? personaThumbUrl(tile.imageUrl, thumbSize) : undefined
  const displayImageUrl = thumbFailed || !thumbUrl ? tile.imageUrl : thumbUrl
  const showPhoto = Boolean(displayImageUrl) && !hasFailed

  function handleImageError() {
    if (!thumbFailed && thumbUrl && thumbUrl !== tile.imageUrl) {
      setThumbFailed(true)
      return
    }
    setHasFailed(true)
  }

  function handleSelectClick() {
    onSelect?.(tile.personaId)
  }

  const photo = showPhoto ? (
    <FadeImage
      src={displayImageUrl as string}
      alt=""
      loading="lazy"
      onError={handleImageError}
      className="absolute inset-0 h-full w-full object-cover object-top"
      data-testid="portrait-mosaic-tile-photo"
    />
  ) : (
    <div
      className="absolute inset-0 h-full w-full"
      data-testid="portrait-mosaic-tile-fallback"
    >
      <UserAvatar
        initials={tile.initials}
        name={tile.name}
        size="lg"
        className={PORTRAIT_MOSAIC_FALLBACK_AVATAR_CLASS}
      />
    </div>
  )

  const tileProps = {
    className: cn(
      'relative block h-full w-full overflow-hidden transition-[filter,opacity,box-shadow] duration-500 ease-out',
      PORTRAIT_MOSAIC_TILE_ASPECT_CLASS,
      tone === 'dark' ? 'bg-[hsl(var(--platform-deep))]' : 'bg-muted',
      portraitMosaicTileStateClass({ isHighlighted: tile.isHighlighted, isSpeaker }),
      isSpeaker && 'ring-2 ring-inset ring-[hsl(var(--brand-accent,var(--primary)))]',
      // A quoted tile reveals itself on hover: it lifts out of the grayscale crowd
      // to full color, so the faces that DO respond to a click are discoverable
      // ("not clickable", live QA 2026-09-03 — the affordance was cursor-only).
      onSelect &&
        'cursor-pointer hover:grayscale-0 hover:opacity-100 focus-visible:grayscale-0 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[hsl(var(--brand-accent,var(--primary)))]',
    ),
    'data-testid': `portrait-mosaic-tile-${index}`,
    'data-persona-id': tile.personaId,
    'data-highlighted': tile.isHighlighted ? 'true' : 'false',
    'data-speaker': isSpeaker ? 'true' : 'false',
    'data-image-source': showPhoto ? tile.imageSource : 'none',
  }

  // Mount pop-in (S4, D6) lives on this OUTER wrapper only — never on the inner
  // tile div below. That div's dim/lit state is a Tailwind `grayscale opacity-40`
  // class swap animated by its own CSS `transition-[filter,opacity]`; if framer
  // also drove `opacity` on that same element, its inline style (final state
  // opacity: 1) would permanently win over the `opacity-40` class after the
  // pop-in finished, breaking the dim/lit distinction for every tile. Reduced
  // motion renders straight at the `visible` state — no hidden flash, no delay.
  return (
    <motion.div
      // `min-h-0 min-w-0` override the grid item's default `min-height/width: auto`
      // (CSS Grid stretches an item to its content's min-content size before applying
      // `aspect-ratio`, same as flex items). The button inside is inline-block with a
      // native line-height, so without this the mount box measured ~15% TALLER than
      // wide (row rhythm visibly bigger than column rhythm, live QA 2026-09-03) even
      // though the shared 3:4 aspect and the grid's own `gap-0.5` were both already
      // correct. Photo/fallback are absolutely positioned so an `<img>` intrinsic
      // size cannot blow a mixed row past that box.
      className={cn(PORTRAIT_MOSAIC_TILE_ASPECT_CLASS, 'min-h-0 min-w-0')}
      initial={shouldReduceMotion ? false : TILE_POP_IN_VARIANTS.hidden}
      animate={TILE_POP_IN_VARIANTS.visible}
      transition={tilePopInTransition(index)}
      data-testid={`portrait-mosaic-tile-mount-${index}`}
    >
      {onSelect ? (
        <button
          type="button"
          {...tileProps}
          aria-label={tile.name}
          aria-pressed={isSpeaker}
          onClick={handleSelectClick}
        >
          {photo}
        </button>
      ) : (
        <div {...tileProps}>{photo}</div>
      )}
    </motion.div>
  )
}
